
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InvitationFormProps {
  resourceId: string;
  topic: string;
  company: string;
  onInvitationsSent: () => Promise<void>;
  onClose: () => void;
}

export function InvitationForm({
  resourceId,
  topic,
  company,
  onInvitationsSent,
  onClose
}: InvitationFormProps) {
  const [emails, setEmails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  const PRODUCTION_URL = "https://everleytraining.com";

  const handleShare = async () => {
    try {
      setIsLoading(true);
      const emailList = emails.split(',').map(email => email.trim()).filter(Boolean);
      if (emailList.length === 0) {
        toast({
          title: "No emails provided",
          description: "Please enter at least one email address.",
          variant: "destructive"
        });
        return;
      }

      // Get existing invitations to avoid duplicates
      const { data: existingInvitationsData } = await supabase
        .from('resource_invitations')
        .select('email')
        .eq('resource_id', resourceId);
      
      const existingEmails = new Set((existingInvitationsData || []).map(invite => invite.email));
      const newEmails = emailList.filter(email => !existingEmails.has(email));
      
      if (newEmails.length === 0) {
        toast({
          title: "No new emails",
          description: "All provided emails have already been invited.",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('invite-trainees', {
        body: {
          resourceId,
          emails: newEmails,
          topic,
          company,
          redirectUrl: PRODUCTION_URL
        }
      });

      if (error) throw error;
      await onInvitationsSent();
      
      const skippedCount = emailList.length - newEmails.length;
      let successMessage = `Resource shared with ${newEmails.length} new recipient(s).`;
      if (skippedCount > 0) {
        successMessage += ` ${skippedCount} email(s) were skipped as they were already invited.`;
      }
      
      toast({
        title: "Resource shared",
        description: successMessage
      });
      
      onClose();
      setEmails("");
    } catch (error) {
      console.error('Error sharing resource:', error);
      toast({
        title: "Error sharing resource",
        description: "There was a problem sharing the resource. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Input 
          placeholder="Enter trainee email addresses, separated by commas" 
          value={emails} 
          onChange={e => setEmails(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Enter one or more email addresses, separated by commas. New users will receive an invitation to create an account.
        </p>
      </div>
      <Button onClick={handleShare} disabled={isLoading} className="w-full">
        {isLoading ? "Sharing..." : "Share Resource"}
      </Button>
    </div>
  );
}
